import * as timeutil from './timeutil';

export type SlotTimerBasis = 'slot' | 'checkin';

export type SlotTimerState =
    | { kind: 'running'; minsLeft: number; endsAt: number }
    | { kind: 'timesUp'; endsAt: number };

interface SlotTimerRecord {
    date: number;
    startTime: number;
    endTime: number;
}

interface SlotTimerSettings {
    type?: string;
    enableSlotTimer?: boolean;
    slotTimerBasis?: SlotTimerBasis;
    slotTimerEnabledFrom?: number;
}

const CHECKIN_CHECKOUT = 'checkin_checkout';

/**
 * Puts the hour/minute of `time` onto the calendar day of `date`.
 * Batch interval times and time-picker check-ins carry an unrelated date.
 */
export const atTimeOnDate = (time: number, date: number): number => {
    const t = timeutil.getInstance(time);
    return timeutil.getInstance(date)
        .hours(t.hours())
        .minutes(t.minutes())
        .seconds(0)
        .milliseconds(0)
        .valueOf();
};

/** Slot start and end on the session date; an end at or before the start runs past midnight. */
export const getSlotBoundsForDate = (record: SlotTimerRecord) => {
    const start = atTimeOnDate(record.startTime, record.date);
    let end = atTimeOnDate(record.endTime, record.date);
    if (end <= start) end = timeutil.addTime(timeutil.getInstance(end), 1, 'day').valueOf();
    return { start, end };
};

/**
 * Countdown shown on the admin Attendance page for a participant row.
 * Returns null when no pill should be shown.
 */
export const getSlotTimerState = (
    record: SlotTimerRecord,
    attendance: Record<string, any> | null | undefined,
    status: string,
    settings: SlotTimerSettings | null | undefined,
    now: number,
): SlotTimerState | null => {
    if (!settings?.enableSlotTimer || !record || !attendance) return null;
    if (status !== 'PRESENT') return null;
    if (typeof attendance.latestCheckOut === 'number') return null;

    const sessionDay = timeutil.getInstance(record.date).startOf('day').valueOf();
    if (typeof settings.slotTimerEnabledFrom === 'number' && sessionDay < settings.slotTimerEnabledFrom) return null;

    const { start, end: slotEnd } = getSlotBoundsForDate(record);
    let endsAt = slotEnd;

    if (settings.type === CHECKIN_CHECKOUT) {
        // Stays until check-out, even on past dates, so missed check-outs stand out.
        if (typeof attendance.latestCheckIn !== 'number') return null;
        if (settings.slotTimerBasis === 'checkin') {
            endsAt = atTimeOnDate(attendance.latestCheckIn, record.date) + (slotEnd - start);
        }
    } else {
        // Present/Absent has no check-out, so the pill clears at midnight of the session day.
        const dayEnd = timeutil.getInstance(record.date).endOf('day').valueOf();
        if (now < sessionDay || now > dayEnd) return null;
    }

    const minsLeft = Math.ceil((endsAt - now) / 60000);
    return minsLeft > 0 ? { kind: 'running', minsLeft, endsAt } : { kind: 'timesUp', endsAt };
};
